import React, { useMemo } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  preRegistrationsApi,
  type PreRegistrationStatus,
} from "../api/preRegistrations";
import { qk } from "../../../../lib/queryKeys";
import { useClientScope } from "../../../../shared/contexts";
import { SectionCard } from "../../settings/components/SectionCard";
import { buildCsv, downloadCsv } from "../../utils/csv";

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

function initials(name: string, email: string): string {
  if (name.trim()) {
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

export const PreRegistrations: React.FC = () => {
  const queryClient = useQueryClient();
  const { isCentralAdmin, effectiveClientId } = useClientScope();

  const [answersOpen, setAnswersOpen] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState<string | null>(null);
  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([]);
  const [activeStatusTab, setActiveStatusTab] = React.useState<PreRegistrationStatus>("pending");
  const [actionSummary, setActionSummary] = React.useState<{
    severity: "success" | "warning";
    message: string;
  } | null>(null);

  // Delete confirmation dialog
  const [deleteConfirmEmails, setDeleteConfirmEmails] = React.useState<string[] | null>(null);

  // Recipient add dialog
  const [recipientDialogOpen, setRecipientDialogOpen] = React.useState(false);
  const [recipientForm, setRecipientForm] = React.useState({ name: "", email: "" });
  const [recipientError, setRecipientError] = React.useState<string | null>(null);

  // ── Queries ──────────────────────────────────────────────────────────────

  const listQuery = useQuery({
    queryKey: qk.preRegistrations(effectiveClientId ?? null, activeStatusTab),
    queryFn: () => preRegistrationsApi.list(effectiveClientId, activeStatusTab),
  });
  const entries = useMemo(() => listQuery.data?.data ?? [], [listQuery.data]);

  const recipientsQuery = useQuery({
    queryKey: ["pre-registration-notification-recipients", isCentralAdmin ? "client-all" : effectiveClientId ?? null],
    queryFn: () =>
      preRegistrationsApi.listNotificationRecipients(
        isCentralAdmin
          ? { scope: "client", client_id: effectiveClientId }
          : undefined
      ),
  });

  const answersQuery = useQuery({
    queryKey: ["pre-registration-answers", selectedEmail],
    queryFn: () => (selectedEmail ? preRegistrationsApi.getAnswers(selectedEmail) : null),
    enabled: Boolean(selectedEmail),
  });

  // Sync selection when entries change
  React.useEffect(() => {
    setSelectedEmails((current) => {
      if (current.length === 0) return current;
      const next = current.filter((email) => entries.some((entry) => entry.email === email));
      return next.length === current.length ? current : next;
    });
  }, [entries]);

  React.useEffect(() => {
    setSelectedEmails([]);
  }, [activeStatusTab]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      const failed: string[] = [];
      let approved = 0;
      for (const email of emails) {
        try {
          await preRegistrationsApi.approve(email);
          approved += 1;
        } catch {
          failed.push(email);
        }
      }
      return { approved, failed };
    },
    onSuccess: (result) => {
      setActionSummary({
        severity: result.failed.length ? "warning" : "success",
        message: `Approved ${result.approved} registration${result.approved === 1 ? "" : "s"}${result.failed.length ? `. Failed: ${result.failed.join(", ")}` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: ["pre-registrations"] });
      setSelectedEmails([]);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (email: string) => preRegistrationsApi.reject(email),
    onSuccess: (result, email) => {
      setActionSummary({
        severity: result.email_sent === false ? "warning" : "success",
        message:
          result.email_sent === false
            ? `Rejected ${email}, but email failed to send: ${result.email_error || "Unknown error"}`
            : `Rejected ${email}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["pre-registrations"] });
      setSelectedEmails((current) => current.filter((item) => item !== email));
    },
    onError: (_error, email) => {
      setActionSummary({ severity: "warning", message: `Failed to reject ${email}.` });
    },
  });

  const createRecipientMutation = useMutation({
    mutationFn: () =>
      preRegistrationsApi.createNotificationRecipient({
        name: recipientForm.name.trim() || undefined,
        email: recipientForm.email.trim(),
        client_id: effectiveClientId,
      }),
    onSuccess: () => {
      setRecipientForm({ name: "", email: "" });
      setRecipientError(null);
      setRecipientDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["pre-registration-notification-recipients"] });
    },
    onError: (err: any) => {
      setRecipientError(err?.response?.data?.message ?? "Failed to add recipient.");
    },
  });

  const deleteRecipientMutation = useMutation({
    mutationFn: (recipientId: string) => preRegistrationsApi.deleteNotificationRecipient(recipientId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pre-registration-notification-recipients"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      const failed: string[] = [];
      let deleted = 0;
      for (const email of emails) {
        try {
          await preRegistrationsApi.softDelete(email);
          deleted += 1;
        } catch {
          failed.push(email);
        }
      }
      return { deleted, failed };
    },
    onSuccess: (result) => {
      setActionSummary({
        severity: result.failed.length ? "warning" : "success",
        message: `Deleted ${result.deleted} registration${result.deleted === 1 ? "" : "s"}${result.failed.length ? `. Failed: ${result.failed.join(", ")}` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: ["pre-registrations"] });
      setSelectedEmails([]);
    },
  });

  const allSelected = entries.length > 0 && selectedEmails.length === entries.length;
  const hasSelection = selectedEmails.length > 0;
  const recipients = recipientsQuery.data?.data ?? [];

  const handleExportCsv = () => {
    if (entries.length === 0) return;
    const headers =
      activeStatusTab === "interest"
        ? ["Name", "Email", "Interested in", "Submitted"]
        : ["Name", "Email", "Client", "Submitted"];
    const rows = entries.map((entry) => {
      const name = entry.name || `${entry.first_name ?? ""} ${entry.last_name ?? ""}`.trim();
      if (activeStatusTab === "interest") {
        return [name, entry.email, entry.interest_note ?? "", formatDateTime(entry.submitted_at)];
      }
      return [name, entry.email, entry.client_name || entry.client_id || "", formatDateTime(entry.submitted_at)];
    });
    const csv = buildCsv(headers, rows);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadCsv(`pre-registrations-${activeStatusTab}-${stamp}.csv`, csv);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      {/* Header + tabs */}
      <Box mb={2}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            Pre-Registrations
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            disabled={listQuery.isLoading || entries.length === 0}
            onClick={handleExportCsv}
          >
            Export CSV
          </Button>
        </Stack>
        <Tabs
          value={activeStatusTab}
          onChange={(_, v) => setActiveStatusTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            minHeight: 40,
            "& .MuiTab-root": { minHeight: 40, py: 0 },
          }}
        >
          <Tab value="pending" label="Pending" sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
          <Tab value="interest" label="Interest Registered" sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
          <Tab value="rejected" label="Rejected" sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }} />
        </Tabs>
      </Box>

      {!isCentralAdmin && !effectiveClientId && (
        <Alert severity="warning" sx={{ mb: 2 }}>No client assigned to your account.</Alert>
      )}

      {actionSummary && (
        <Alert
          severity={actionSummary.severity}
          sx={{ mb: 2 }}
          onClose={() => setActionSummary(null)}
        >
          {actionSummary.message}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Notification recipients */}
        <SectionCard
          title="Notification recipients"
          description={
            isCentralAdmin
              ? "Client-specific recipients. Global recipients are managed in Settings → Notification Recipients."
              : "These addresses receive a notification when a new pre-registration is submitted for your organisation."
          }
          action={
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setRecipientForm({ name: "", email: "" });
                setRecipientError(null);
                setRecipientDialogOpen(true);
              }}
            >
              Add recipient
            </Button>
          }
        >
          {recipientsQuery.isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Stack>
          ) : recipientsQuery.isError ? (
            <Alert severity="error">Failed to load notification recipients.</Alert>
          ) : recipients.length === 0 ? (
            <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
              <NotificationsNoneIcon sx={{ fontSize: 32, opacity: 0.3, mb: 0.5 }} />
              <Typography variant="body2">No recipients configured.</Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />} spacing={0}>
              {recipients.map((r) => (
                <Stack
                  key={r.id}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ py: 1.25 }}
                >
                  <Avatar
                    sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 600, bgcolor: "primary.main", flexShrink: 0 }}
                  >
                    {initials(r.name || "", r.email)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {r.name ? (
                      <>
                        <Typography variant="body2" fontWeight={500} noWrap>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">{r.email}</Typography>
                      </>
                    ) : (
                      <Typography variant="body2" noWrap>{r.email}</Typography>
                    )}
                  </Box>
                  {r.client_name && (
                    <Chip label={r.client_name} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                  )}
                  <Tooltip title="Remove recipient">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deleteRecipientMutation.isPending}
                      onClick={() => deleteRecipientMutation.mutate(r.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}
        </SectionCard>

        {/* Bulk action bar */}
        {hasSelection && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: "primary.main",
              borderRadius: 1.5,
              color: "primary.contrastText",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2" sx={{ flex: 1, color: "inherit" }}>
                {selectedEmails.length} selected
              </Typography>
              {activeStatusTab !== "interest" && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={approveMutation.isPending ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon fontSize="small" />}
                  disabled={approveMutation.isPending}
                  onClick={() => { setActionSummary(null); approveMutation.mutate(selectedEmails); }}
                  sx={{ bgcolor: "white", color: "success.dark", "&:hover": { bgcolor: "grey.100" } }}
                >
                  Approve {selectedEmails.length}
                </Button>
              )}
              {isCentralAdmin && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  onClick={() => setDeleteConfirmEmails(selectedEmails)}
                  sx={{ bgcolor: "white", color: "error.dark", "&:hover": { bgcolor: "grey.100" } }}
                >
                  Delete {selectedEmails.length}
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                onClick={() => setSelectedEmails([])}
                sx={{ color: "primary.contrastText" }}
              >
                Deselect
              </Button>
            </Stack>
          </Box>
        )}

        {/* List */}
        {listQuery.isLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Stack>
        ) : listQuery.isError ? (
          <Alert severity="error">Failed to load pre-registrations.</Alert>
        ) : entries.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            <HowToRegOutlinedIcon sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
            <Typography variant="body2">
              {activeStatusTab === "pending"
                ? "No pending pre-registrations."
                : activeStatusTab === "interest"
                ? "No interest registrations."
                : "No rejected pre-registrations."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 600, fontSize: 12, py: 1.25 } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={hasSelection && !allSelected}
                      onChange={(e) =>
                        setSelectedEmails(e.target.checked ? entries.map((entry) => entry.email) : [])
                      }
                      inputProps={{ "aria-label": "select all" }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  {activeStatusTab !== "interest" && <TableCell>Client</TableCell>}
                  {activeStatusTab === "interest" && <TableCell>Interested in</TableCell>}
                  <TableCell>Submitted</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const checked = selectedEmails.includes(entry.email);
                  const ini = initials(entry.name || "", entry.email);
                  return (
                    <TableRow key={entry.email} hover sx={{ "& td": { fontSize: 13, py: 1 } }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() =>
                            setSelectedEmails((current) =>
                              current.includes(entry.email)
                                ? current.filter((e) => e !== entry.email)
                                : [...current, entry.email]
                            )
                          }
                          inputProps={{ "aria-label": `select ${entry.email}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 600, bgcolor: "primary.main", flexShrink: 0 }}>
                            {ini}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {entry.name || "—"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>{entry.email}</Typography>
                      </TableCell>
                      {activeStatusTab !== "interest" && (
                        <TableCell>
                          <Typography variant="body2">{entry.client_name || entry.client_id || "—"}</Typography>
                        </TableCell>
                      )}
                      {activeStatusTab === "interest" && (
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{entry.interest_note || "—"}</Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {formatDateTime(entry.submitted_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {activeStatusTab !== "interest" && (
                            <Tooltip title="View answers">
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedEmail(entry.email); setAnswersOpen(true); }}
                              >
                                <QuizOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {activeStatusTab === "pending" && (
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={rejectMutation.isPending}
                                onClick={() => { setActionSummary(null); rejectMutation.mutate(entry.email); }}
                              >
                                <CancelOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {activeStatusTab === "rejected" && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                disabled={approveMutation.isPending}
                                onClick={() => { setActionSummary(null); approveMutation.mutate([entry.email]); }}
                              >
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isCentralAdmin && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteConfirmEmails([entry.email])}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Stack>

      {/* Add recipient dialog */}
      <Dialog open={recipientDialogOpen} onClose={() => setRecipientDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add notification recipient</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Name (optional)"
              value={recipientForm.name}
              onChange={(e) => setRecipientForm((p) => ({ ...p, name: e.target.value }))}
            />
            <TextField
              size="small"
              fullWidth
              label="Email"
              type="email"
              value={recipientForm.email}
              onChange={(e) => setRecipientForm((p) => ({ ...p, email: e.target.value }))}
              autoFocus
              required
              onKeyDown={(e) => { if (e.key === "Enter" && recipientForm.email.trim()) createRecipientMutation.mutate(); }}
            />
            {recipientError && <Alert severity="error">{recipientError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!recipientForm.email.trim() || createRecipientMutation.isPending}
            onClick={() => createRecipientMutation.mutate()}
          >
            {createRecipientMutation.isPending ? <CircularProgress size={18} /> : "Add recipient"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Answers dialog */}
      <Dialog
        open={answersOpen}
        onClose={() => { setAnswersOpen(false); setSelectedEmail(null); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Pre-registration answers</DialogTitle>
        <DialogContent>
          {answersQuery.isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading answers…</Typography>
            </Stack>
          ) : answersQuery.isError ? (
            <Alert severity="error">Failed to load answers.</Alert>
          ) : answersQuery.data?.data ? (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Box
                sx={{
                  p: 1.25,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  {answersQuery.data.data.email}
                  {answersQuery.data.data.client_name && ` · ${answersQuery.data.data.client_name}`}
                </Typography>
              </Box>
              <Stack spacing={1.5} divider={<Divider />}>
                {answersQuery.data.data.answers.map((answer) => {
                  const value = Array.isArray(answer.answer)
                    ? answer.answer.join(", ")
                    : answer.answer ?? "—";
                  return (
                    <Box key={answer.question_id}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                        {answer.title}
                      </Typography>
                      <Typography variant="body2">{String(value)}</Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No answers found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAnswersOpen(false); setSelectedEmail(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmEmails !== null}
        onClose={() => setDeleteConfirmEmails(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete {deleteConfirmEmails?.length === 1
              ? `the pre-registration for ${deleteConfirmEmails[0]}`
              : `${deleteConfirmEmails?.length} pre-registrations`}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmEmails(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => {
              if (deleteConfirmEmails) {
                setActionSummary(null);
                deleteMutation.mutate(deleteConfirmEmails);
                setDeleteConfirmEmails(null);
              }
            }}
          >
            {deleteMutation.isPending ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
